import { Box, createStyles, Flex, Skeleton, Stack, Tabs } from '@mantine/core';
import type { IntegrationTab } from '@repo/common/constants/integration/integrations.preferences';
import { getSupportedTabs } from '@repo/common/constants/integration/integrations.preferences';
import { UserRole } from '@repo/common/enums/UserRole';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { titleCase } from '@repo/common/utils';
import { Title } from '@repo/foundations';
import { space } from '@repo/theme/primitives';
import { AxiosError } from 'axios';
import { capitalize, isNil } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import {
	useAuthUser,
	useIntegration,
	useIntegrationSpec,
	useSuspenseFeatureAccess,
} from '../../../api';
import { useUpdateWorkspace } from '../../../api/hooks/workspace/useUpdateWorkspace.ts';
import { useAiWorkspaceSettingEnabled } from '../../../hooks/useAiWorkspaceSettingEnabled.ts';
import type { WorkspaceNotificationPreference } from '../../../lib/models/workspace.ts';
import ErrorPage from '../../../pages/ErrorPage/ErrorPage';
import type { ErrorType } from '../../../pages/ErrorPage/ErrorPage.types';
import { useV2CheckIAMPermission } from '../../../utils/authorization/roles.ts';
import { FeatureGuard } from '../../FeatureGuard/FeatureGuard';
import { FullWidthLoadingSpinner } from '../../LoadingSpinner';
import { IntegrationTitle } from '../components/IntegrationTitle';
import IntegrationAlertConfig from '../IntegrationAlertConfig';
import { IntegrationImport } from '../IntegrationImport';
import { IntegrationLookerGit } from '../IntegrationLookerProjects';
import IntegrationMapping from '../IntegrationMapping';
import { IntegrationPopularity } from '../IntegrationPopularity';
import { ConnectionStep } from '../IntegrationSelectionPage/StepComponents';
import IntegrationSlackAIPreferences from '../IntegrationSlackAIPreferences';
import IntegrationSlackChannels from '../IntegrationSlackChannels';
import IntegrationSlackNotifications from '../IntegrationSlackNotifications';
import { IntegrationSlackSearchPreferences } from '../IntegrationSlackSearchPreferences/IntegrationSlackSearchPreferences.tsx';
import { IntegrationPullRequests } from './IntegrationPullRequests.tsx';
import {
	PreferencesPanel,
	SchedulePanel,
	SelectPanel,
	SyncPanel,
} from './TabPanels';

interface IntegrationPageProps {
	id: string;
}

const useStyles = createStyles(
	(theme, { fullWidth }: { fullWidth: boolean }) => ({
		wrapper: {
			flexWrap: 'nowrap',
			width: '100%',
			height: '100%',
			padding: `0 ${theme.spacing.xl}`,
			gap: theme.spacing['3xl'],
		},
		tabsWrapper: {
			alignItems: 'flex-start',
			width: theme.other.width.sm,
			height: '100%',
			overflowY: 'auto',
			gap: theme.spacing.sm,
		},
		tabsHeader: {
			padding: `${theme.spacing['3xs']} ${theme.spacing.sm}`,
		},
		tabsRoot: {
			width: '100%',
			height: '100%',
		},
		tabsList: {
			width: '100%',
			gap: theme.spacing['5xs'],
		},
		tab: {
			backgroundColor: theme.other.getColor('fill/transparent/default'),
			padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
			fontSize: theme.other.typography.text.sm,
			fontWeight: theme.other.typography.weight.semibold,
			color: theme.other.getColor('text/secondary/default'),
			'&:hover': {
				backgroundColor: theme.other.getColor('fill/transparent/hover'),
			},
			'&:focus, &:active': {
				backgroundColor: theme.other.getColor('fill/transparent/active'),
				color: theme.other.getColor('text/primary/default'),
			},
		},
		panelWrapper: {
			width: '100%',
			justifyContent: 'center',
			overflowY: 'auto',
		},
		panel: {
			alignItems: 'flex-start',
			maxWidth: fullWidth ? '100%' : theme.other.width.lg,
			height: '100%',
			padding: `0 ${theme.spacing.xs}`,
			margin: '0 auto',
		},
		tabDescription: {
			fontSize: theme.other.typography.text.sm,
			color: theme.other.getColor('text/secondary/default'),
		},
	})
);

function IntegrationPage({ id }: IntegrationPageProps) {
	const [tabs, setTabs] = useState<IntegrationTab[]>();
	const [activeTab, setActiveTab] = useState<string>();

	const navigate = useNavigate();

	const { workspace, isAdminUser } = useAuthUser();
	const { secodaCoreAccess } = useSuspenseFeatureAccess();
	const { mutateAsync: updateWorkspace } = useUpdateWorkspace(workspace.id);

	const [notificationSettings, setNotificationSettings] = useState<
		WorkspaceNotificationPreference | undefined
	>(undefined);

	useEffect(() => {
		if (workspace.notification_preferences) {
			setNotificationSettings(workspace.notification_preferences);
		}
	}, [workspace.notification_preferences]);

	const handleNotificationSettingsChange = useCallback(
		(notificationSettings: WorkspaceNotificationPreference) => {
			updateWorkspace({
				data: {
					id: workspace.id,
					notification_preferences: notificationSettings,
				},
			});
		},
		[updateWorkspace, workspace.id]
	);

	const enableAi = useAiWorkspaceSettingEnabled();
	const { classes, theme } = useStyles({
		fullWidth: activeTab === 'Pull requests',
	});

	const { tab: tabParam } = useParams();

	const { data: integration, error: integrationError } = useIntegration({
		id,
		options: {
			enabled: !!id,
		},
	});

	const integrationSpec = useIntegrationSpec(id);

	const hasIntegrationManageIAMPermission = useV2CheckIAMPermission({
		v2Permission: 'Integrations.Manage',
		defaultResult: true,
	});

	const isExtension = useMemo(
		() =>
			integrationSpec?.type === 'builtin' && integrationSpec?.value.isExtension,
		[integrationSpec]
	);

	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		if (integrationSpec && !tabs) {
			const supportedTabs = getSupportedTabs(integrationSpec, {
				isAIEnabledWorkspace: enableAi,
				isExtensionReadOnly:
					isExtension && !(hasIntegrationManageIAMPermission && isAdminUser),
				secodaCoreAccess: !!secodaCoreAccess,
			}).filter((tab) => !tab.hidden);
			setTabs(supportedTabs);
			// Only set active tab if not already set or if current active tab is not in the new tabs
			if (!activeTab || !supportedTabs.find((tab) => tab.name === activeTab)) {
				setActiveTab(supportedTabs[0]?.name);
			}
		}
	}, [
		integrationSpec,
		enableAi,
		isExtension,
		hasIntegrationManageIAMPermission,
		isAdminUser,
		secodaCoreAccess,
		activeTab,
		tabs,
	]);

	// Set the selected tab based on the browser URL.
	useEffect(() => {
		if (tabParam && tabs) {
			const found = tabs?.findIndex(
				(f) => f.name.toLowerCase() === tabParam.toLowerCase()
			);
			if (!isNil(found)) {
				// Use `isNil` because it can be 0, which will resolve to false.
				setActiveTab(tabs[found].name);
			}
		}
	}, [tabParam, tabs]);

	const handleTabChange = useCallback(
		(tabName: string) => {
			setActiveTab(tabName);
			navigate(`/integrations/${integration?.id}/${tabName.toLowerCase()}`);
		},
		[navigate, integration?.id]
	);

	const getTabName = useCallback((integrationTab: IntegrationTab) => {
		if (integrationTab.preserveCase) {
			return integrationTab.displayName || integrationTab.name;
		}
		return titleCase(integrationTab.displayName || integrationTab.name);
	}, []);

	if (integrationError instanceof AxiosError) {
		return (
			<ErrorPage
				code={(integrationError.response?.status as ErrorType) ?? 404}
			/>
		);
	}

	// Editors can see "Extensions"
	if (!isAdminUser && !isExtension) {
		return null;
	}

	if (!tabs || !integrationSpec || !integration) {
		return <FullWidthLoadingSpinner />;
	}

	return (
		<FeatureGuard
			featureName="Integrations"
			description="Get access to integrations to seamlessly interact, govern and view lineage with your data."
			illustrationName="integrations"
			v1AllowedRoles={[UserRole.ADMIN, UserRole.EDITOR]}
			v2Permission="Integrations.Read"
			isFeatureIncludedInPlan={true}
			isAlwaysIncludedFeature={false}
		>
			<Tabs
				classNames={{
					root: classes.tabsRoot,
					tabsList: classes.tabsList,
					tab: classes.tab,
					panel: classes.panel,
				}}
				orientation="vertical"
				variant="pills"
				value={activeTab}
				onTabChange={handleTabChange}
				keepMounted={false}
			>
				<Flex className={classes.wrapper}>
					<Stack className={classes.tabsWrapper}>
						<IntegrationTitle
							integrationSpec={integrationSpec}
							integration={integration}
							titleSize={'md'}
						/>
						<Tabs.List>
							{tabs.map((tab) => (
								<Tabs.Tab key={tab.name} value={tab.name}>
									{getTabName(tab)}
								</Tabs.Tab>
							))}
						</Tabs.List>
					</Stack>
					<Flex className={classes.panelWrapper} id="integration-page-scroll">
						{tabs.map((tab) => (
							<Tabs.Panel
								id={`panel-${tab.name}`}
								key={tab.name}
								value={tab.name}
							>
								<Stack spacing="2xl">
									<Stack spacing={theme.spacing['3xs']}>
										<Title size="xl">{tab.title ?? capitalize(tab.name)}</Title>
										<Box className={classes.tabDescription}>
											{tab.description}
										</Box>
									</Stack>
									{/* SYNC TAB */}
									{tab.name === 'syncs' && (
										<Box pb="5xl">
											<SyncPanel
												integration={integration}
												spec={integrationSpec}
											/>
										</Box>
									)}
									{/* SCHEDULE TAB */}
									{tab.name === 'schedule' && (
										<Box pb="5xl">
											<SchedulePanel
												integration={integration}
												spec={integrationSpec}
											/>
										</Box>
									)}
									{/* MAPPING TAB */}
									{tab.name === 'mappings' && (
										<Box pb="5xl">
											<IntegrationMapping integration={integration as any} />
										</Box>
									)}
									{/* GROUPS + SCHEMAS TAB */}
									{(tab.name === 'groups' || tab.name === 'schemas') && (
										<Box pb="5xl">
											<SelectPanel
												integration={integration}
												spec={integrationSpec}
											/>
										</Box>
									)}
									{/* POPULARITY TAB */}
									{tab.name === 'popularity' && (
										<Box pb="5xl">
											<IntegrationPopularity />
										</Box>
									)}
									{/* PROJECTS TAB */}
									{tab.name === 'project' && (
										<Box pb="5xl">
											<IntegrationLookerGit integration={integration} />
										</Box>
									)}
									{/* IMPORT TAB */}
									{tab.name === 'import' && (
										<Box pb="5xl">
											<IntegrationImport integration={integration} />
										</Box>
									)}
									{/* NOTIFICATIONS TAB */}
									{tab.name === 'notifications' && notificationSettings && (
										<Box pb="5xl">
											<IntegrationSlackNotifications
												notificationSettings={notificationSettings}
												onNotificationSettingsChange={
													handleNotificationSettingsChange
												}
											/>
										</Box>
									)}
									{/* CHANNELS TAB */}
									{tab.name === 'channels' && integration.id && (
										<Box pb="5xl">
											<IntegrationSlackChannels
												integrationId={integration.id}
											/>
										</Box>
									)}
									{/* AI TAB */}
									{tab.name === 'AI' && integration.id && (
										<Box pb="5xl">
											<IntegrationSlackAIPreferences
												integrationId={integration.id}
											/>
										</Box>
									)}
									{/* SLACK SEARCH TAB */}
									{tab.name === 'slack-search' && integration.id && (
										<Box pb="5xl">
											<IntegrationSlackSearchPreferences
												integrationId={integration.id}
											/>
										</Box>
									)}
									{/* PREFERENCES TAB */}
									{tab.name === 'preferences' && (
										<PreferencesPanel
											integration={integration}
											spec={integrationSpec}
											options={tab.options || []}
										/>
									)}
									{tab.name === 'alerts' && integration?.id && (
										<Box pb="5xl">
											<IntegrationAlertConfig integrationId={integration.id} />
										</Box>
									)}
									{/* PULL REQUESTS TAB */}
									{tab.name === 'Pull requests' && (
										<Box pb="5xl">
											<IntegrationPullRequests integration={integration} />
										</Box>
									)}
									{/* CONNECTION TAB */}
									{tab.name === 'connection' && (
										<Box pb="5xl">
											<ConnectionStep
												integrationSpec={integrationSpec}
												integration={integration}
												hideTitle
												readonly={
													isExtension &&
													!(hasIntegrationManageIAMPermission && isAdminUser)
												}
											/>
										</Box>
									)}
								</Stack>
							</Tabs.Panel>
						))}
					</Flex>
				</Flex>
			</Tabs>
		</FeatureGuard>
	);
}

function IntegrationPageWrapper() {
	const { id } = useParams();

	if (!id) {
		return (
			<Flex p={space[4]} pb="5xl">
				<Skeleton />
			</Flex>
		);
	}

	return <IntegrationPage id={id} />;
}

export default IntegrationPageWrapper;
